/**
 * atlas-card-product.js
 * Handles price fallback calculation for product cards with builder steps.
 * Called per-card via window.atlasCardProductInit(elementId).
 */

(function () {
    // --- utils ---
    const safeJSON = (s) => {
        if (!s) return null;
        try {
            return JSON.parse(s);
        } catch (e) {
            return null;
        }
    };
    const formatMoney = (cents, format) => {
        try {
            const value = (cents / 100).toFixed(2);
            const withComma = value.replace(".", ",");
            const withDelims = value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            let out = format || "${{amount}}";
            out = out.replace(
                /\{\{\s*amount_no_decimals\s*\}\}/g,
                Math.round(cents / 100)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            );
            out = out.replace(
                /\{\{\s*amount_with_comma_separator\s*\}\}/g,
                withComma
            );
            out = out.replace(/\{\{\s*amount\s*\}\}/g, withDelims);
            return out;
        } catch (e) {
            return `${cents / 100}`;
        }
    };
    const parseCents = (v) => {
        if (v == null) return null;
        if (typeof v === "number") return v;
        if (typeof v === "string") {
            const m = v.match(/[0-9]+(?:[.,][0-9]{1,2})?/);
            if (m) {
                const num = parseFloat(m[0].replace(",", "."));
                if (!isNaN(num)) return Math.round(num * 100);
            }
            const asNum = Number(v);
            if (!Number.isNaN(asNum) && asNum > 0) return Math.round(asNum);
            return null;
        }
        if (typeof v === "object") {
            if (typeof v.amount === "number") return Math.round(v.amount * 100);
            if (typeof v.amount === "string") {
                const m = v.amount.match(/[0-9]+(?:[.,][0-9]{1,2})?/);
                if (m) {
                    const num = parseFloat(m[0].replace(",", "."));
                    if (!isNaN(num)) return Math.round(num * 100);
                }
            }
        }
        return null;
    };

    const findFirstHandle = (obj) => {
        if (!obj) return null;
        if (
            obj.options &&
            Array.isArray(obj.options) &&
            obj.options.length
        ) {
            const first = obj.options[0];
            if (first && typeof first === "object" && first.handle)
                return first.handle;
        }
        const arrays = obj.values || obj.items || obj.list;
        if (Array.isArray(arrays) && arrays.length) {
            const first = arrays[0];
            if (first && typeof first === "object" && first.handle)
                return first.handle;
            if (typeof first === "string") return first;
        }
        return null;
    };

    const deriveSizeKeyFromHandle = (h) => {
        if (!h || typeof h !== "string") return null;
        const m = h.match(/(\d+)\s*-?\s*cm/i);
        if (m) return (m[1] + "cm").toLowerCase();
        const tail = h.split("classic-cake-size-").pop();
        if (tail)
            return tail.replace(/[^0-9a-z]/gi, "").toLowerCase();
        return null;
    };

    const findFirstNameOverride = (obj) => {
        if (!obj) return null;
        if (
            obj.options &&
            Array.isArray(obj.options) &&
            obj.options.length
        ) {
            const first = obj.options[0];
            if (
                first &&
                typeof first === "object" &&
                first.name_override
            )
                return String(first.name_override);
        }
        return null;
    };

    const findPriceByHandle = (handle, primaryMap, secondaryMap, tertiaryMap) => {
        if (!handle) return null;
        const tryMaps = [primaryMap, secondaryMap, tertiaryMap];
        for (const map of tryMaps) {
            if (!map) continue;
            const arr = Array.isArray(map)
                ? map
                : map.options || map.values || map.items || map.list;
            if (Array.isArray(arr)) {
                for (const x of arr) {
                    if (
                        x &&
                        typeof x === "object" &&
                        (x.handle === handle ||
                            x.id === handle ||
                            x.key === handle ||
                            x.slug === handle)
                    ) {
                        if (typeof x.price_cents === "number")
                            return x.price_cents;
                        if (typeof x.priceCents === "number")
                            return x.priceCents;
                        if (typeof x.price === "number")
                            return Math.round(x.price * 100);
                        if (typeof x.price === "string") {
                            const m = x.price.match(
                                /[0-9]+(?:[.,][0-9]{1,2})?/
                            );
                            if (m) {
                                const num = parseFloat(
                                    m[0].replace(",", ".")
                                );
                                if (!isNaN(num))
                                    return Math.round(num * 100);
                            }
                        }
                    }
                }
            }
            if (map && typeof map === "object" && !Array.isArray(map)) {
                const val = map[handle];
                if (typeof val === "number")
                    return val >= 100 ? val : Math.round(val * 100);
                if (typeof val === "string") {
                    const m = val.match(/[0-9]+(?:[.,][0-9]{1,2})?/);
                    if (m) {
                        const num = parseFloat(m[0].replace(",", "."));
                        if (!isNaN(num))
                            return Math.round(num * 100);
                    }
                }
            }
        }
        return null;
    };

    function initCardProduct(elementId) {
        const el = document.getElementById(elementId);
        if (!el) return;

        const dropLiquidFallback = () => {
            try {
                const parent = el && el.parentElement;
                if (!parent) return;
                // Remove any server-rendered fallback prices to avoid duplicate prices
                parent
                    .querySelectorAll(
                        ".price--fallback:not(.price--fallback-js)"
                    )
                    .forEach((node) => {
                        if (node && node.parentElement)
                            node.parentElement.removeChild(node);
                    });
            } catch (e) {
                // no-op
            }
        };

        // --- read datasets ---
        const step1 = safeJSON(el.getAttribute("data-step1"));
        const step2 = safeJSON(el.getAttribute("data-step2"));
        const step3 = safeJSON(el.getAttribute("data-step3"));
        const priceMap = safeJSON(el.getAttribute("data-price-map"));
        const step2Prices =
            safeJSON(el.getAttribute("data-step2-prices")) || null;
        const genericPrices =
            safeJSON(el.getAttribute("data-prices")) || null;
        const variants =
            safeJSON(el.getAttribute("data-variants")) || [];
        const moneyFormat = el.getAttribute("data-money-format");
        const priceMinCents =
            Number(el.getAttribute("data-price-min")) || 0;
        const firstVariantPriceCents =
            Number(el.getAttribute("data-first-variant-price")) || 0;
        const manualFallbackCents =
            Number(el.getAttribute("data-fallback-cents")) || 0;
        const liquidFallbackCents =
            Number(el.getAttribute("data-liquid-fallback-cents")) || 0;

        const steps = [
            ["step_2", step2],
            ["step_1", step1],
            ["step_3", step3],
        ];

        // priceMap, step2Prices, genericPrices validation removed debug output

        let chosen = null;
        for (const [key, obj] of steps) {
            const h = findFirstHandle(obj);
            if (h) {
                chosen = { key, handle: h };
                break;
            }
        }

        let cents = null;
        if (chosen) {
            cents = findPriceByHandle(
                chosen.handle,
                priceMap,
                step2Prices,
                genericPrices
            );
        }

        // --- fallback to first variant or min price if needed ---
        if (!(typeof cents === "number" && cents > 0)) {
            if (firstVariantPriceCents > 0) {
                cents = firstVariantPriceCents;
            } else if (priceMinCents > 0) {
                cents = priceMinCents;
            } else if (liquidFallbackCents > 0) {
                cents = liquidFallbackCents;
            } else if (manualFallbackCents > 0) {
                cents = manualFallbackCents;
            }
        }

        if (!(typeof cents === "number" && cents > 0)) {
            const sizeKey =
                chosen && chosen.handle
                    ? deriveSizeKeyFromHandle(chosen.handle)
                    : null;
            const nameOverride = findFirstNameOverride(step2);
            const nameLc = nameOverride
                ? String(nameOverride).toLowerCase()
                : null;
            const norm = (s) =>
                (s || "")
                    .toString()
                    .toLowerCase()
                    .replace(/\s+/g, "")
                    .replace(/[-()]/g, "");
            const rawLc = (s) => (s || "").toString().toLowerCase();

            const makeAlts = (k) => {
                if (!k) return [];
                const d = k.replace(/cm$/i, "");
                const num = d.replace(/[^0-9]/g, "");
                return [
                    k,
                    num ? num + " cm" : k,
                    num ? num + "-cm" : k,
                    (num ? num + "cm" : k).toUpperCase(),
                    num ? num + " CM" : k,
                    num ? num + "-CM" : k,
                ].filter(Boolean);
            };
            const sizeAlts = makeAlts(sizeKey);

            let matchedVariant = null;
            for (const v of variants) {
                const o1 = v.option1,
                    o2 = v.option2,
                    o3 = v.option3;
                const o1n = norm(o1),
                    o2n = norm(o2),
                    o3n = norm(o3);
                const o1lc = rawLc(o1),
                    o2lc = rawLc(o2),
                    o3lc = rawLc(o3);

                let hitSize = false;
                if (sizeAlts.length) {
                    for (const alt of sizeAlts) {
                        const altn = norm(alt);
                        if (
                            (o1 && o1n.includes(altn)) ||
                            (o2 && o2n.includes(altn)) ||
                            (o3 && o3n.includes(altn))
                        ) {
                            hitSize = true;
                            break;
                        }
                    }
                }
                const hitName =
                    nameLc &&
                    (o1lc === nameLc || o2lc === nameLc || o3lc === nameLc);
                if (hitSize || hitName) {
                    matchedVariant = v;
                    break;
                }
            }
            if (
                matchedVariant &&
                typeof matchedVariant.price === "number" &&
                matchedVariant.price > 0
            ) {
                cents = matchedVariant.price;
            } else {
                const positives = variants
                    .map((v) => (v ? parseCents(v.price) : null))
                    .filter((v) => typeof v === "number" && v > 0);
                const minPos = positives.length
                    ? Math.min.apply(null, positives)
                    : null;
                if (typeof minPos === "number" && minPos > 0)
                    cents = minPos;
            }
        }

        if (!(typeof cents === "number" && cents > 0) && priceMinCents > 0) {
            cents = priceMinCents;
        }
        if (
            !(typeof cents === "number" && cents > 0) &&
            liquidFallbackCents > 0
        ) {
            cents = liquidFallbackCents;
        }
        if (
            !(typeof cents === "number" && cents > 0) &&
            manualFallbackCents > 0
        ) {
            cents = manualFallbackCents;
        }
        if (!(typeof cents === "number" && cents > 0)) {
            dropLiquidFallback();
            el.textContent = "—";
            el.hidden = false;
        }
        if (typeof cents === "number" && cents > 0) {
            dropLiquidFallback();
            el.textContent = formatMoney(cents, moneyFormat);
            el.hidden = false;
        } else {
            dropLiquidFallback();
        }
    }

    // Register globally so inline config scripts can call it
    window.atlasCardProductInit = initCardProduct;

    // Also process any already-queued calls (in case script loads after inline calls)
    if (Array.isArray(window.atlasCardProductQueue)) {
        window.atlasCardProductQueue.forEach(initCardProduct);
        window.atlasCardProductQueue = [];
    }
})();
